import { Box, IconButton, Typography } from "@mui/material";
import { makeStyles } from "tss-react/mui";
import CircularProgress from "@mui/material/CircularProgress";
import { ChevronLeft, ChevronRight } from "@mui/icons-material";
import dayjs from "dayjs";
import { FC, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Colors } from "../../style/Colors";
import { getReportingPeriodParts } from "../../utils/reporting-period-label.util";
import { IPomReportingListItem } from "../../domain/reporting/pom-reporting";
import { PomReportingStatus } from "../../repositories/models/reportings/pom-reporting-status";

const useStyles = makeStyles()({
  container: {
    background: Colors.greenDark,
    borderRadius: "8px",
    padding: "25px 28px 32px 28px",
    minWidth: "360px",
  },
  yearNavigation: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: "16px",
  },
  yearTitle: {
    color: Colors.white,
    fontWeight: "bold",
    fontSize: "1.1rem",
    margin: "0 16px",
  },
  yearNavButton: {
    color: Colors.white,
    "&.Mui-disabled": {
      color: "rgba(255, 255, 255, 0.3)",
    },
  },
  entryContainer: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr 1fr",
    gridGap: "12px",
  },
  entry: {
    padding: "8px 12px",
    textAlign: "center",
    color: Colors.white,
    cursor: "pointer",
    borderRadius: "22px",
    border: "2px solid transparent",
    transition: "transform 200ms, border-radius 200ms, background 200ms",
    minHeight: "48px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    "&:hover": {
      background: Colors.greenLight,
      borderRadius: "6px",
      transform: "scale(1.08)",
    },
  },
  entryOpen: {
    borderColor: Colors.white,
  },
  entryReported: {
    background: Colors.greenLight,
  },
  entryActive: {
    background: Colors.white,
    borderRadius: "4px",
    color: Colors.greenDark,
    "&:hover": {
      color: Colors.white,
    },
  },
  entryInRange: {
    background: "rgba(255, 255, 255, 0.2)",
  },
  emptyText: {
    color: Colors.white,
    textAlign: "center",
    padding: "20px",
  },
  loadingContainer: {
    height: "240px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  entryDate: {
    fontSize: "0.85rem",
    lineHeight: 1.3,
  },
  draftIndicator: {
    position: "absolute",
    top: "0px",
    right: "0px",
    width: "12px",
    height: "12px",
    borderRadius: "50%",
    backgroundColor: Colors.orange,
    border: "2px solid white",
    pointerEvents: "none",
  },
});

interface IPomReportingPeriodPickerProps {
  reportingList: IPomReportingListItem[];
  currentReportingNo?: number;
  isLoading: boolean;
  draftReportingNos: number[];
  onReportingSelected?: (reportingNo: number) => void;
  // Range mode
  mode?: "range";
  startReportingNo?: number;
  endReportingNo?: number;
  onRangeSelected?: (startNo: number | undefined, endNo: number | undefined) => void;
}

export const PomReportingPeriodPicker: FC<IPomReportingPeriodPickerProps> = (props) => {
  const {
    reportingList,
    currentReportingNo,
    isLoading,
    draftReportingNos,
    onReportingSelected,
    mode,
    startReportingNo,
    endReportingNo,
    onRangeSelected,
  } = props;
  const { classes } = useStyles();
  const { t } = useTranslation();

  const availableYears = useMemo((): number[] => {
    const years = new Set<number>();
    reportingList.forEach((item) => {
      years.add(dayjs(item.reportingPeriodStartDate).year());
    });
    return Array.from(years).sort((a, b) => a - b);
  }, [reportingList]);

  const [currentYearIndex, setCurrentYearIndex] = useState<number>(0);

  // Sync year page with the currently selected reporting period
  useEffect(() => {
    if (availableYears.length === 0) return;
    const targetNo = mode === "range" ? startReportingNo : currentReportingNo;
    if (targetNo !== undefined) {
      const selected = reportingList.find((r) => r.reportingNo === targetNo);
      if (selected) {
        const year = dayjs(selected.reportingPeriodStartDate).year();
        const idx = availableYears.indexOf(year);
        if (idx >= 0) {
          setCurrentYearIndex(idx);
          return;
        }
      }
    }
    // In range mode: default to the year of the period that contains today
    if (mode === "range") {
      const today = dayjs();
      const current = reportingList.find(
        (r) => !today.isBefore(dayjs(r.reportingPeriodStartDate)) && !today.isAfter(dayjs(r.reportingPeriodEndDate))
      );
      if (current) {
        const year = dayjs(current.reportingPeriodStartDate).year();
        const idx = availableYears.indexOf(year);
        if (idx >= 0) {
          setCurrentYearIndex(idx);
          return;
        }
      }
    }
    // Fallback: latest year
    setCurrentYearIndex(availableYears.length - 1);
  }, [availableYears, currentReportingNo, startReportingNo, mode, reportingList]);

  const currentYear = availableYears[currentYearIndex];

  const filteredReportingList = useMemo((): IPomReportingListItem[] => {
    if (currentYear === undefined) return [];
    return reportingList.filter((item) => dayjs(item.reportingPeriodStartDate).year() === currentYear);
  }, [reportingList, currentYear]);

  const handlePrevYear = (): void => {
    setCurrentYearIndex((prev) => Math.max(0, prev - 1));
  };

  const handleNextYear = (): void => {
    setCurrentYearIndex((prev) => Math.min(availableYears.length - 1, prev + 1));
  };

  const handleEntryClick = (reportingNo: number): void => {
    if (mode === "range") {
      if (!onRangeSelected) return;
      if (startReportingNo === undefined || (startReportingNo !== undefined && endReportingNo !== undefined)) {
        // No selection or both selected: set as new start
        onRangeSelected(reportingNo, undefined);
      } else if (reportingNo === startReportingNo) {
        // Clicked on start: deselect
        onRangeSelected(undefined, undefined);
      } else if (reportingNo < startReportingNo) {
        // Clicked before start: use as new start
        onRangeSelected(reportingNo, undefined);
      } else {
        // Clicked after start: set as end
        onRangeSelected(startReportingNo, reportingNo);
      }
    } else {
      onReportingSelected?.(reportingNo);
    }
  };

  const getEntryClass = (item: IPomReportingListItem): string => {
    const classNames = [classes.entry];

    if (mode === "range") {
      const isStart = item.reportingNo === startReportingNo;
      const isEnd = item.reportingNo === endReportingNo;
      const isInRange =
        startReportingNo !== undefined &&
        endReportingNo !== undefined &&
        item.reportingNo > startReportingNo &&
        item.reportingNo < endReportingNo;

      if (isStart || isEnd) {
        classNames.push(classes.entryActive);
      } else if (isInRange) {
        classNames.push(classes.entryInRange);
      } else {
        classNames.push(classes.entryOpen);
      }
    } else {
      if (item.reportingNo === currentReportingNo) {
        classNames.push(classes.entryActive);
      } else {
        switch (item.status) {
          case PomReportingStatus.Reported:
          case PomReportingStatus.Corrected:
            classNames.push(classes.entryReported);
            break;
          default:
            classNames.push(classes.entryOpen);
            break;
        }
      }
    }

    return classNames.join(" ");
  };

  return (
    <div className={classes.container}>
      {isLoading && (
        <div className={classes.loadingContainer}>
          <CircularProgress size={50} />
        </div>
      )}
      {!isLoading && reportingList.length === 0 && (
        <Typography className={classes.emptyText}>{t("pom.reportings.period_picker.empty")}</Typography>
      )}
      {!isLoading && reportingList.length > 0 && (
        <>
          <Box className={classes.yearNavigation}>
            <IconButton
              onClick={handlePrevYear}
              disabled={currentYearIndex === 0}
              className={classes.yearNavButton}
              size="small"
            >
              <ChevronLeft />
            </IconButton>
            <Typography className={classes.yearTitle}>{currentYear}</Typography>
            <IconButton
              onClick={handleNextYear}
              disabled={currentYearIndex === availableYears.length - 1}
              className={classes.yearNavButton}
              size="small"
            >
              <ChevronRight />
            </IconButton>
          </Box>
          <Box className={classes.entryContainer}>
            {filteredReportingList.map((item) => (
              <Box
                key={item.reportingNo}
                className={getEntryClass(item)}
                onClick={() => handleEntryClick(item.reportingNo)}
                style={{ position: "relative" }}
              >
                {draftReportingNos.includes(item.reportingNo) && (
                  <span className={classes.draftIndicator} />
                )}
                <div className={classes.entryDate}>
                  {getReportingPeriodParts(item.reportingPeriodStartDate, item.reportingPeriodEndDate).lines.map(
                    (line, idx) => (
                      <div key={idx}>{line}</div>
                    ),
                  )}
                </div>
              </Box>
            ))}
          </Box>
        </>
      )}
    </div>
  );
};
